import { resolveFieldHints } from '@/lib/docx/sub-field-hints';
import { TemplateVariable } from '../../types/templates.types';

type DocxVariableMetaType = {
  /** Person and company children share this key; the PARENT's Type says which union it is. */
  subKind?: SubKind | CompanySubKind;
  isSignatory?: boolean;
  signingOrder?: number | null;
  companyRef?: string | null;
  companyLabel?: string | null;
  valueCopyFrom?: string;
  defaultSigner?: {
    name?: string | null;
    email?: string | null;
    role?: string | null;
    company?: string | null;
  } | null;
};

/** Text-context anchor persisted on a variable (esp. a signature sub-field). */
export interface DocxSourceSpan {
  docPart?: string;
  paragraphIndex: number | null;
  originalText: string;
  contextBefore: string;
  contextAfter: string;
}

/** Parse a serialized SourceSpan JSON, tolerating malformed/absent values. */
function parseSourceSpan(raw: string | null | undefined): DocxSourceSpan | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<DocxSourceSpan>;
    if (!s || typeof s.originalText !== 'string') return null;
    return {
      docPart: s.docPart,
      paragraphIndex: s.paragraphIndex ?? null,
      originalText: s.originalText,
      contextBefore: s.contextBefore ?? '',
      contextAfter: s.contextAfter ?? '',
    };
  } catch {
    return null;
  }
}

export const buildDocxVariable = (variables: TemplateVariable[]): FillVariable[] => {
  // A child's slot is read as a person SubKind or a company CompanySubKind depending on
  // its parent row's Type, so resolve parent types before mapping.
  const typeById = new Map(variables.map((v) => [v.id, v.type]));
  // Sort by the admin-defined Order so the fill form renders fields in the intended sequence.
  return [...variables]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((v) => {
      const meta = v.typeMeta ? (JSON.parse(v.typeMeta) as DocxVariableMetaType) : {};
      const isCompanyChild = !!v.parentId && typeById.get(v.parentId) === 'company';
      const subKind = isCompanyChild ? undefined : (meta.subKind as SubKind | undefined);
      const companySubKind = isCompanyChild
        ? (meta.subKind as CompanySubKind | undefined)
        : undefined;
      // Sub-field hints are not stored — a slot's placeholder and help text are the same for
      // every template, so they are resolved here rather than duplicated onto every row.
      // Doing it in the mapper means all the field renderers get them without changes.
      const hints = resolveFieldHints({
        placeholder: v.placeholder,
        helpText: v.helpText,
        subKind,
        companySubKind,
      });
      return {
        id: v.id,
        parentId: v.parentId as string | null,
        name: v.name as string,
        label: v.label as string,
        type: v.type as string,
        required: v.required as boolean,
        options: v.options ?? [],
        helpText: hints.helpText ?? null,
        placeholder: hints.placeholder ?? null,
        order: v.order,
        subKind,
        companySubKind,
        isSignatory: meta.isSignatory ?? false,
        signingOrder: meta.signingOrder ?? null,
        companyRef: meta.companyRef ?? null,
        companyLabel: meta.companyLabel ?? null,
        valueCopyFrom: meta.valueCopyFrom ?? null,
        defaultSigner: meta.defaultSigner ?? null,
        sourceSpan: parseSourceSpan(v.sourceSpan),
      };
    });
};

export type SubKind = 'name' | 'role' | 'company' | 'email' | 'phone' | 'address' | 'signature';

/**
 * The sub-fields a Company entity decomposes into — the company counterpart of `SubKind`.
 * Must stay in sync with the builder's list in `docx-builder/selection/types.ts`.
 * A Company never signs, so there is no `signature` slot.
 */
export type CompanySubKind = 'name' | 'address' | 'website' | 'email' | 'phone';

export const COMPANY_SUB_LABELS: Record<CompanySubKind, string> = {
  name: 'Company name',
  address: 'Address',
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
};

export const COMPANY_SUB_ORDER: Record<CompanySubKind, number> = {
  name: 0,
  address: 1,
  website: 2,
  email: 3,
  phone: 4,
};

/** A snapshot of suggested signer details set by the template author (frozen, not a live link). */
export interface DefaultSigner {
  name?: string | null;
  email?: string | null;
  role?: string | null;
  company?: string | null;
}

export interface FillVariable {
  id: string;
  parentId: string | null;
  name: string;
  label: string;
  /** Scalar field types, plus "person" for a grouping parent. */
  type: string;
  required: boolean;
  options: string[];
  helpText: string | null;
  placeholder: string | null;
  /** Admin-defined display order (flat index across all rows). */
  order?: number;
  // Person child marker.
  subKind?: SubKind;
  /** Company child marker. Mutually exclusive with `subKind`. */
  companySubKind?: CompanySubKind;
  // Person parent metadata (only on type === "person").
  isSignatory?: boolean;
  signingOrder?: number | null;
  companyRef?: string | null;
  companyLabel?: string | null;
  /** Source field name this field mirrors — filled once, fanned out at merge (plain fields). */
  valueCopyFrom?: string | null;
  defaultSigner?: DefaultSigner | null;
  /** Text-context anchor for this field (signature sub-fields use it as the signing location). */
  sourceSpan?: DocxSourceSpan | null;
}

/** A signatory resolved from the filled form — shown in the preview panel's signer list. */
export interface ResolvedSignatory {
  /** The person parent's variable name (stable key). */
  key: string;
  name: string;
  email: string;
  role: string;
  company: string;
  signingOrder: number | null;
  /** Text-context anchor of this signatory's signature sub-field, if marked. */
  signatureAnchor?: DocxSourceSpan | null;
}

export const SUB_LABELS: Record<SubKind, string> = {
  name: 'Name',
  role: 'Role',
  company: 'Company',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  signature: 'Signature',
};
export const SUB_ORDER: Record<SubKind, number> = {
  name: 0,
  role: 1,
  company: 2,
  email: 3,
  phone: 4,
  address: 5,
  signature: 6,
};
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const inputTypeFor: Record<string, string> = {
  number: 'number',
  currency: 'number',
  date: 'date',
  email: 'email',
  url: 'url',
};

export const FLOW_STEPS = [
  { id: 'fill', title: 'Fill in details' },
  { id: 'preview', title: 'Preview & download' },
];

export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Locale-format a single entered value by its field type. Empty → "" (blank token). */
export function formatValue(type: string, raw: unknown, locale: string): string {
  if (raw === null || raw === undefined) return '';
  const s = String(raw);
  if (!s.trim()) return '';

  if (type === 'date') {
    // A date input gives "YYYY-MM-DD"; parse it as a local calendar date so formatting
    // never shifts a day across timezones (new Date("YYYY-MM-DD") is UTC).
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
    const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(s);
    if (!Number.isNaN(d.getTime())) {
      try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(d);
      } catch {
        return s;
      }
    }
    return s;
  }

  if (type === 'number' || type === 'currency') {
    // number/currency inputs submit a canonical dot-decimal value (the HTML number input
    // is locale-independent), so parse directly. Do NOT strip grouping by guesswork —
    // that silently corrupts locale-formatted input (e.g. de "1.234,56").
    const n = Number(s);
    if (!Number.isNaN(n)) {
      try {
        return new Intl.NumberFormat(
          locale,
          type === 'currency'
            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            : { maximumFractionDigits: 4 }
        ).format(n);
      } catch {
        return s;
      }
    }
    return s; // not a clean number — leave it untouched
  }

  return s;
}
